import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { store } from './store';
import type { MemberDTO } from '@shared/types';

const STORAGE_KEY = 'archivio.member';

interface MembersCtx {
  members: MemberDTO[];
  activeId: string | null;
  active: MemberDTO | null;
  setActive: (id: string) => void;
  reload: () => void;
  loading: boolean;
}

const Ctx = createContext<MembersCtx | null>(null);

/**
 * Profilo attivo (§6.15). Filtra documenti/garanzie per `owner_member_id`.
 * I documenti restano sotto l'unico account; i familiari sono membri.
 */
export function MemberProvider({ children }: { children: ReactNode }) {
  const [members, setMembers] = useState<MemberDTO[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    store
      .members()
      .then((items) => {
        if (!alive) return;
        setMembers(items);
        // Default = "self" se l'id persistito non è più valido.
        setActiveId((cur) => {
          if (cur && items.some((m) => m.id === cur)) return cur;
          return items.find((m) => m.is_self)?.id ?? items[0]?.id ?? null;
        });
      })
      .catch(() => alive && setMembers([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [nonce]);

  const setActive = useCallback((id: string) => {
    setActiveId(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignora */ }
  }, []);

  const active = members.find((m) => m.id === activeId) ?? null;

  return (
    <Ctx.Provider value={{ members, activeId, active, setActive, reload, loading }}>
      {children}
    </Ctx.Provider>
  );
}

export function useMembers(): MembersCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMembers deve essere usato dentro <MemberProvider>');
  return ctx;
}

export const RELATION_LABEL: Record<string, string> = {
  self: 'Tu',
  spouse: 'Coniuge',
  child: 'Figlio',
  parent: 'Genitore',
  other: 'Familiare',
};
